"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.FxCoreDeclarativeAgentPart = void 0;
const tslib_1 = require("tslib");
const hooks_1 = require("@feathersjs/hooks");
const m365_spec_parser_1 = require("@microsoft/m365-spec-parser");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const path = tslib_1.__importStar(require("path"));
const daSpecParser_1 = require("../common/daSpecParser");
const globalVars_1 = require("../common/globalVars");
const localizeUtils_1 = require("../common/localizeUtils");
const telemetry_1 = require("../common/telemetry");
const versionMetadata_1 = require("../common/versionMetadata");
const actionInjector_1 = require("../component/configManager/actionInjector");
const constants_1 = require("../component/constants");
const errors_1 = require("../component/driver/teamsApp/errors");
const results_1 = require("../component/driver/teamsApp/results");
const CopilotGptManifestUtils_1 = require("../component/driver/teamsApp/utils/CopilotGptManifestUtils");
const ManifestUtils_1 = require("../component/driver/teamsApp/utils/ManifestUtils");
const PluginManifestUtils_1 = require("../component/driver/teamsApp/utils/PluginManifestUtils");
const utils_1 = require("../component/driver/teamsApp/utils/utils");
const helper_1 = require("../component/generator/declarativeAgent/helper");
const helper_2 = require("../component/generator/openApiSpec/helper");
const questionMW_1 = require("../component/middleware/questionMW");
const common_1 = require("../component/utils/common");
const mcpToolFetcher_1 = require("../component/utils/mcpToolFetcher");
const pathUtils_1 = require("../component/utils/pathUtils");
const common_2 = require("../error/common");
const constants_2 = require("../question/constants");
const teamsProjectTypeNode_1 = require("../question/scaffold/vsc/teamsProjectTypeNode");
const concurrentLocker_1 = require("./middleware/concurrentLocker");
const errorHandler_1 = require("./middleware/errorHandler");
// Non-translatable CLI command template used in warning messages
const mcpAddActionHint = "atk add action --api-plugin-type mcp --mcp-da-server-url <server-url> --mcp-tools-file-path <path-to-tools-json> --interactive false";
class FxCoreDeclarativeAgentPart {
    async updateActionWithMCP(inputs) {
        const context = (0, globalVars_1.createContext)();
        const projectPath = inputs.projectPath;
        if (!projectPath) {
            throw new Error("projectPath is undefined"); // should never happen
        }
        let aiPluginFilePath = inputs[constants_2.QuestionNames.PluginManifestFilePath];
        // If the user picked the "Create a new ai-plugin.json" option in the question,
        // the value will be the sentinel. Generate a fresh skeleton manifest under
        // appPackage using the file name from the follow-up text question and
        // register it as a new action of the declarative agent before continuing
        // with the regular update flow.
        if (aiPluginFilePath === teamsProjectTypeNode_1.CreateNewPluginManifestSentinel) {
            const desiredFileName = inputs[constants_2.QuestionNames.NewPluginManifestFileName] || "ai-plugin.json";
            const teamsManifestPath = path.join(projectPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.ManifestTemplateFileName);
            const manifestRes = await ManifestUtils_1.manifestUtils._readAppManifest(teamsManifestPath);
            if (manifestRes.isErr()) {
                return (0, teamsfx_api_1.err)(manifestRes.error);
            }
            const declarativeAgentRelativePath = manifestRes.value?.copilotAgents?.declarativeAgents?.[0]?.file;
            if (!declarativeAgentRelativePath) {
                return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.TeamsAppRequiredPropertyMissingError.name, errors_1.AppStudioError.TeamsAppRequiredPropertyMissingError.message("declarativeAgents", teamsManifestPath)));
            }
            const declarativeAgentManifestPath = path.join(projectPath, teamsfx_api_1.AppPackageFolderName, declarativeAgentRelativePath);
            const createRes = await (0, helper_1.createNewActionPluginManifest)(projectPath, desiredFileName, declarativeAgentManifestPath);
            if (createRes.isErr()) {
                return (0, teamsfx_api_1.err)(createRes.error);
            }
            aiPluginFilePath = createRes.value.pluginManifestPath;
            inputs[constants_2.QuestionNames.PluginManifestFilePath] = aiPluginFilePath;
        }
        if (!(await fs_extra_1.default.pathExists(aiPluginFilePath))) {
            const error = new teamsfx_api_1.SystemError("MCPForDAPluginManifestNotFound", "PluginManifestNotFound", (0, localizeUtils_1.getDefaultString)("core.MCPForDA.pluginManifestNotFound", aiPluginFilePath), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.pluginManifestNotFound", aiPluginFilePath));
            return (0, teamsfx_api_1.err)(error);
        }
        const aiPluginFilePathRelative = path.basename(aiPluginFilePath);
        const mcpServerUrl = inputs[constants_2.QuestionNames.MCPForDAServerUrl];
        const serverName = inputs[constants_2.QuestionNames.MCPForDAServerName];
        const mcpAuth = inputs[constants_2.QuestionNames.MCPForDAAuth];
        const authType = inputs[constants_2.QuestionNames.MCPForDAAuthType];
        let oauthAuthorizationUrl = undefined;
        let oauthTokenUrl = undefined;
        let oauthRefreshUrl = undefined;
        let registrationId = undefined;
        if (mcpAuth === "OAuthPluginVault") {
            try {
                registrationId = `MCP_DA_AUTH_ID_${serverName.toUpperCase()}`;
                if (authType === "oauth") {
                    const metadata = await (0, mcpToolFetcher_1.resolveMCPOAuthMetadata)(inputs[constants_2.QuestionNames.MCPForDAAuthMetadataUrl], inputs[constants_2.QuestionNames.MCPForDAAuthWellKnownUrl]);
                    oauthAuthorizationUrl = metadata.authorizationUrl;
                    oauthTokenUrl = metadata.tokenUrl;
                    oauthRefreshUrl = metadata.refreshUrl;
                }
            }
            catch (error) {
                void context.userInteraction.showMessage("error", (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.mcpAuthMetadataMissingError", error.message), false);
            }
        }
        // 2. Read ai-plugin.json
        const aiPluginContent = await fs_extra_1.default.readJSON(aiPluginFilePath);
        // For dynamic fetch tools, keep the functions empty and add runtime info
        // TODO: support dynamic fetch tools in the future
        const mcpToolsDetail = inputs[constants_2.QuestionNames.MCPForDAAvailableTools];
        const mcpToolsSelected = inputs[constants_2.QuestionNames.MCPForDAPreFetchTools];
        if (!mcpToolsDetail || !mcpToolsSelected) {
            const error = new teamsfx_api_1.UserError("MCPForDAPreFetchToolsNotFound", "PreFetchToolsNotFound", (0, localizeUtils_1.getDefaultString)("core.MCPForDA.preFetchToolsNotFound"), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.preFetchToolsNotFound"));
            return (0, teamsfx_api_1.err)(error);
        }
        const toolsSelectedPrevious = [];
        aiPluginContent.runtimes
            .filter((runtime) => (runtime.type === "RemoteMCPServer" && runtime.spec.url === mcpServerUrl) ||
            runtime.type === "LocalPlugin")
            .forEach((runtime) => {
            toolsSelectedPrevious.push(...runtime.run_for_functions);
        });
        aiPluginContent.functions = aiPluginContent.functions.filter((func) => !toolsSelectedPrevious.includes(func.name));
        aiPluginContent.functions = [
            ...aiPluginContent.functions,
            ...mcpToolsDetail
                .filter((tool) => mcpToolsSelected.includes(tool.name))
                .map((tool) => {
                if (inputs[constants_2.QuestionNames.MCPLocalServerIdentifier] != null) {
                    return {
                        name: tool.name,
                        description: tool.description,
                        parameters: {
                            type: tool.inputSchema.type || "object",
                            properties: tool.inputSchema.properties,
                            required: tool.inputSchema.required || [],
                        },
                    };
                }
                else {
                    return {
                        name: tool.name,
                        description: tool.description,
                    };
                }
            }),
        ];
        const matchedRuntime = aiPluginContent.runtimes.find((runtime) => runtime.type === "RemoteMCPServer" && runtime.spec.url === mcpServerUrl);
        aiPluginContent.runtimes = aiPluginContent.runtimes.filter((runtime) => (runtime.type !== "RemoteMCPServer" && runtime.type !== "LocalPlugin") ||
            runtime.spec.url !== mcpServerUrl);
        if (inputs[constants_2.QuestionNames.MCPLocalServerIdentifier] != null) {
            aiPluginContent.runtimes.push({
                type: "LocalPlugin",
                spec: {
                    local_endpoint: `${constants_1.LocalMcpPrefix}${inputs[constants_2.QuestionNames.MCPLocalServerIdentifier]}`,
                },
                run_for_functions: mcpToolsSelected,
            });
        }
        else {
            let mcpFile = matchedRuntime?.spec.mcp_tool_description?.file;
            if (!mcpFile) {
                mcpFile = "mcp-tools.json";
                let suffix = 1;
                while (await fs_extra_1.default.pathExists(path.join(path.dirname(aiPluginFilePath), mcpFile))) {
                    mcpFile = `mcp-tools-${suffix}.json`;
                    suffix += 1;
                }
            }
            await fs_extra_1.default.writeJSON(path.join(path.dirname(aiPluginFilePath), mcpFile), {
                tools: [
                    ...mcpToolsDetail
                        .filter((tool) => mcpToolsSelected.includes(tool.name))
                        .map((tool) => {
                        return {
                            ...tool,
                            title: tool.name
                                .replace(/_/g, " ")
                                .replace(/^./, (str) => str.toUpperCase()),
                        };
                    }),
                ],
            }, { spaces: 4 });
            aiPluginContent.runtimes.push({
                type: "RemoteMCPServer",
                spec: {
                    url: mcpServerUrl,
                    mcp_tool_description: {
                        file: mcpFile,
                    },
                },
                run_for_functions: mcpToolsSelected,
                auth: mcpAuth === "OAuthPluginVault" && !!registrationId
                    ? {
                        type: "OAuthPluginVault",
                        reference_id: `$\{\{${registrationId}\}\}`,
                    }
                    : {
                        type: "None",
                    },
            });
        }
        if (mcpAuth === "OAuthPluginVault" && !!registrationId) {
            // insert oauth info in teamsapp.yaml
            await actionInjector_1.ActionInjector.injectCreateOAuthActionForMCP(pathUtils_1.pathUtils.getYmlFilePath(projectPath), authType, serverName, registrationId, mcpServerUrl, oauthAuthorizationUrl, oauthTokenUrl, oauthRefreshUrl);
        }
        void context.userInteraction
            .showMessage("info", (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.updatePluginManifest", aiPluginFilePathRelative), false, "Provision")
            .then((result) => {
            if (result.isOk() && result.value === "Provision") {
                void this.provisionResources(inputs);
            }
        });
        await fs_extra_1.default.writeJSON(aiPluginFilePath, aiPluginContent, { spaces: 4 });
        void context.userInteraction.openFile?.(aiPluginFilePath);
        return (0, teamsfx_api_1.ok)(undefined);
    }
    // This method will be implemented by FxCore
    provisionResources(_inputs) {
        throw new Error("Method not implemented.");
    }
    // These methods will be implemented by FxCore
    parseAuthNameAndScheme(_listResult, _inputs) {
        throw new Error("Method not implemented.");
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async updateAuthActionInYaml(_authName, _authScheme, _projectPath, _apSpecPath, _pluginManifestPath, _forceToAddNew) {
        throw new Error("Method not implemented.");
    }
    /**
     * Add plugin
     */
    async addPlugin(inputs) {
        if (!inputs.projectPath) {
            throw new Error("projectPath is undefined"); // should never happen
        }
        const context = (0, globalVars_1.createContext)();
        const isGenerateFromApiSpec = inputs[constants_2.QuestionNames.ActionType] === constants_2.ActionStartOptions.apiSpec().id;
        const isGenerateFromMCP = inputs[constants_2.QuestionNames.ActionType] === constants_2.ActionStartOptions.mcp().id;
        // VS Code MCP "Add Action" flow: aligned with the "DA with MCP" scaffolding
        // behavior. Only the MCP server URL is collected from the user. The toolkit
        // writes the URL into .vscode/mcp.json and signals the caller to open the
        // file and surface the "start MCP server / Fetch Action" notification, which
        // already exists for the scaffolding flow. The actual plugin manifest and DA
        // action are produced later by the "Update Action with MCP" command.
        //
        // The CLI flow (below) keeps the original non-interactive behavior of
        // probing the MCP server, fetching tools and writing ai-plugin.json + yaml
        // OAuth registration, since the CLI has no equivalent notification UX.
        if (isGenerateFromMCP && inputs.platform === teamsfx_api_1.Platform.VSCode) {
            return await this.addPluginFromMCP(inputs, context);
        }
        const teamsManifestPath = inputs[constants_2.QuestionNames.ManifestPath];
        const appPackageFolder = path.dirname(teamsManifestPath);
        // validate the project is valid for adding plugin
        const manifestRes = await ManifestUtils_1.manifestUtils._readAppManifest(teamsManifestPath);
        if (manifestRes.isErr()) {
            return (0, teamsfx_api_1.err)(manifestRes.error);
        }
        const teamsManifest = manifestRes.value;
        const declarativeGpt = teamsManifest.copilotExtensions
            ? teamsManifest.copilotExtensions.declarativeCopilots?.[0]
            : teamsManifest.copilotAgents?.declarativeAgents?.[0];
        if (!declarativeGpt?.file) {
            return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.TeamsAppRequiredPropertyMissingError.name, errors_1.AppStudioError.TeamsAppRequiredPropertyMissingError.message("declarativeAgents", teamsManifestPath)));
        }
        const gptManifestFilePathRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.getManifestPath(teamsManifestPath);
        if (gptManifestFilePathRes.isErr()) {
            return (0, teamsfx_api_1.err)(gptManifestFilePathRes.error);
        }
        const declarativeCopilotManifestPath = gptManifestFilePathRes.value;
        const declarativeCopilotManifesRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.readCopilotGptManifestFile(declarativeCopilotManifestPath);
        if (declarativeCopilotManifesRes.isErr()) {
            return (0, teamsfx_api_1.err)(declarativeCopilotManifesRes.error);
        }
        const declarativeCopilotManifest = declarativeCopilotManifesRes.value;
        let confirmMessage = (0, localizeUtils_1.getLocalizedString)("core.addApi.confirm", path.relative(inputs.projectPath, appPackageFolder));
        // Will be used if generating from API spec
        let specParser = undefined;
        let authNameAndSchemes = [];
        if (isGenerateFromApiSpec) {
            specParser = new m365_spec_parser_1.SpecParser(inputs[constants_2.QuestionNames.ApiSpecLocation].trim(), (0, helper_2.getParserOptions)(m365_spec_parser_1.ProjectType.Copilot, true));
            const listResult = await (0, daSpecParser_1.listAPIInfo)(inputs[constants_2.QuestionNames.ApiSpecLocation].trim());
            authNameAndSchemes = this.parseAuthNameAndScheme(listResult, inputs);
            if (authNameAndSchemes.length > 0) {
                const doesLocalYamlPathExists = await fs_extra_1.default.pathExists(path.join(inputs.projectPath, versionMetadata_1.MetadataV3.localConfigFile));
                confirmMessage = doesLocalYamlPathExists
                    ? (0, localizeUtils_1.getLocalizedString)("core.addApi.confirm.localTeamsYaml", path.relative(inputs.projectPath, appPackageFolder), versionMetadata_1.MetadataV4.localConfigFile, versionMetadata_1.MetadataV4.configFile)
                    : (0, localizeUtils_1.getLocalizedString)("core.addApi.confirm.teamsYaml", path.relative(inputs.projectPath, appPackageFolder), versionMetadata_1.MetadataV4.configFile);
            }
        }
        // confirm
        const confirmRes = await context.userInteraction.showMessage("warn", confirmMessage, true, (0, localizeUtils_1.getLocalizedString)("core.addApi.continue"));
        if (confirmRes.isErr()) {
            return (0, teamsfx_api_1.err)(confirmRes.error);
        }
        else if (confirmRes.value !== (0, localizeUtils_1.getLocalizedString)("core.addApi.continue")) {
            return (0, teamsfx_api_1.err)(new common_2.UserCancelError());
        }
        // find the next available action id
        let actionId = "";
        let suffix = 1;
        actionId = `action_${suffix}`;
        const existingActionIds = declarativeCopilotManifest.actions?.map((action) => action.id);
        while (existingActionIds?.includes(actionId)) {
            suffix += 1;
            actionId = `action_${suffix}`;
        }
        let destinationPluginManifestPath;
        // generate files
        if (isGenerateFromApiSpec && specParser) {
            destinationPluginManifestPath =
                await CopilotGptManifestUtils_1.copilotGptManifestUtils.getDefaultNextAvailablePluginManifestPath(appPackageFolder, undefined);
            const destinationApiSpecPath = await PluginManifestUtils_1.pluginManifestUtils.getDefaultNextAvailableApiSpecPath(inputs[constants_2.QuestionNames.ApiSpecLocation].trim(), path.join(appPackageFolder, teamsfx_api_1.DefaultApiSpecFolderName));
            const generateRes = await (0, helper_2.generateFromApiSpec)(specParser, teamsManifestPath, inputs, context, teamsfx_api_1.Stage.addPlugin, m365_spec_parser_1.ProjectType.Copilot, {
                destinationApiSpecFilePath: destinationApiSpecPath,
                pluginManifestFilePath: destinationPluginManifestPath,
            }, inputs[constants_2.QuestionNames.ApiSpecLocation].trim());
            if (generateRes.isErr()) {
                return (0, teamsfx_api_1.err)(generateRes.error);
            }
            const warnings = generateRes.value.warnings;
            if (warnings && warnings.length > 0) {
                const warnSummary = await (0, helper_2.generateScaffoldingSummary)(warnings, manifestRes.value, path.relative(inputs.projectPath, destinationApiSpecPath), path.relative(inputs.projectPath, destinationPluginManifestPath), inputs.projectPath);
                context.logProvider.info(warnSummary + "\n");
            }
            const addActionRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.addAction(declarativeCopilotManifestPath, actionId, (0, utils_1.normalizePath)(path.relative(appPackageFolder, destinationPluginManifestPath), true));
            if (addActionRes.isErr()) {
                return (0, teamsfx_api_1.err)(addActionRes.error);
            }
            for (const authNameAndScheme of authNameAndSchemes) {
                await this.updateAuthActionInYaml(authNameAndScheme.authName, authNameAndScheme.authScheme, inputs.projectPath, destinationApiSpecPath, destinationPluginManifestPath);
            }
        }
        else if (isGenerateFromMCP) {
            // CLI MCP action: create ai-plugin.json with MCP runtime config.
            // (VS Code path is handled earlier by addPluginFromMCP.)
            const mcpServerUrl = inputs[constants_2.QuestionNames.MCPForDAServerUrl];
            const toolsFilePath = inputs[constants_2.QuestionNames.MCPToolsFilePath];
            const mcpWarnings = [];
            // Validate: --mcp-da-server-url is required for MCP actions
            if (!mcpServerUrl) {
                return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(teamsfx_api_1.Stage.addPlugin, "MissingMCPServerUrl", (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.missingServerUrl"), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.missingServerUrl")));
            }
            // Load tools from file if provided and not yet loaded
            const existingTools = inputs[constants_2.QuestionNames.MCPForDAAvailableTools];
            if (toolsFilePath && (!existingTools || existingTools.length === 0)) {
                try {
                    const { readMCPToolsFromFile } = await Promise.resolve().then(() => tslib_1.__importStar(require("../component/utils/mcpToolFetcher")));
                    const fileTools = await readMCPToolsFromFile(toolsFilePath);
                    inputs[constants_2.QuestionNames.MCPForDAAvailableTools] = fileTools;
                    if (!inputs[constants_2.QuestionNames.MCPForDAPreFetchTools]) {
                        inputs[constants_2.QuestionNames.MCPForDAPreFetchTools] = fileTools.map((t) => t.name);
                    }
                }
                catch {
                    mcpWarnings.push({
                        type: "mcpToolsFileReadError",
                        content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFileReadError", toolsFilePath, mcpAddActionHint),
                    });
                }
            }
            // Probe auth if tools were loaded from file but auth not yet detected
            if (inputs[constants_2.QuestionNames.MCPForDAAvailableTools]?.length > 0 &&
                !inputs[constants_2.QuestionNames.MCPForDAAuth] &&
                mcpServerUrl) {
                try {
                    const { probeMCPServerAuth } = await Promise.resolve().then(() => tslib_1.__importStar(require("../component/utils/mcpToolFetcher")));
                    const authProbe = await probeMCPServerAuth(mcpServerUrl);
                    if (authProbe.requiresAuth) {
                        inputs[constants_2.QuestionNames.MCPForDAAuth] = "OAuthPluginVault";
                        if (authProbe.authMetadataUrl) {
                            inputs[constants_2.QuestionNames.MCPForDAAuthMetadataUrl] = authProbe.authMetadataUrl;
                        }
                    }
                }
                catch {
                    // Auth probe failed - continue without auth
                }
            }
            // Auto-fetch tools if not yet loaded (CLI flow for no-auth servers)
            const currentTools = inputs[constants_2.QuestionNames.MCPForDAAvailableTools];
            if ((!currentTools || currentTools.length === 0) && mcpServerUrl) {
                try {
                    const { fetchMCPTools } = await Promise.resolve().then(() => tslib_1.__importStar(require("../component/utils/mcpToolFetcher")));
                    const result = await fetchMCPTools(mcpServerUrl);
                    if (!result.requiresAuth && result.tools.length > 0) {
                        inputs[constants_2.QuestionNames.MCPForDAAvailableTools] = result.tools;
                        if (!inputs[constants_2.QuestionNames.MCPForDAPreFetchTools]) {
                            inputs[constants_2.QuestionNames.MCPForDAPreFetchTools] = result.tools.map((t) => t.name);
                        }
                    }
                    else if (result.requiresAuth) {
                        // Store auth metadata for later use
                        inputs[constants_2.QuestionNames.MCPForDAAuth] = "OAuthPluginVault";
                        if (result.authMetadataUrl) {
                            inputs[constants_2.QuestionNames.MCPForDAAuthMetadataUrl] = result.authMetadataUrl;
                        }
                        mcpWarnings.push({
                            type: "mcpAuthRequired",
                            content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.authRequired", mcpServerUrl, mcpAddActionHint),
                        });
                    }
                    else {
                        mcpWarnings.push({
                            type: "mcpNoToolsFetched",
                            content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.noToolsFetched", mcpServerUrl, mcpAddActionHint),
                        });
                    }
                }
                catch {
                    mcpWarnings.push({
                        type: "mcpFetchError",
                        content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.fetchError", mcpServerUrl, mcpAddActionHint),
                    });
                }
            }
            // If no tools available (auth required or fetch failed), skip creating plugin - just warn
            const mcpTools = inputs[constants_2.QuestionNames.MCPForDAAvailableTools];
            const mcpToolsSelected = inputs[constants_2.QuestionNames.MCPForDAPreFetchTools] || [];
            if (!mcpTools || mcpTools.length === 0 || mcpToolsSelected.length === 0) {
                if (mcpWarnings.length > 0) {
                    for (const warning of mcpWarnings) {
                        context.logProvider.warning(warning.content);
                    }
                }
                return (0, teamsfx_api_1.ok)(undefined);
            }
            destinationPluginManifestPath =
                await CopilotGptManifestUtils_1.copilotGptManifestUtils.getDefaultNextAvailablePluginManifestPath(appPackageFolder, undefined);
            const pluginManifest = {
                $schema: "https://developer.microsoft.com/json-schemas/copilot/plugin/v2.4/schema.json",
                schema_version: "v2.4",
                name_for_human: "MCP Action",
                description_for_human: "Action powered by MCP server",
                contact_email: "publisher-email@example.com",
                namespace: "mcpAction",
                functions: [],
                runtimes: [],
            };
            if (mcpTools && mcpTools.length > 0 && mcpToolsSelected.length > 0) {
                const selectedTools = mcpTools.filter((tool) => mcpToolsSelected.includes(tool.name));
                pluginManifest.functions = selectedTools.map((tool) => ({
                    name: tool.name,
                    description: tool.description || "",
                }));
                // Write mcp-tools file with full tool definitions (no field filtering)
                const mcpToolsFile = `mcp-tools-${suffix}.json`;
                const mcpToolsOutputPath = path.join(appPackageFolder, mcpToolsFile);
                await fs_extra_1.default.writeJSON(mcpToolsOutputPath, { tools: selectedTools }, { spaces: 4 });
                pluginManifest.runtimes = [
                    {
                        type: "RemoteMCPServer",
                        spec: {
                            url: mcpServerUrl,
                            mcp_tool_description: {
                                file: mcpToolsFile,
                            },
                        },
                        run_for_functions: pluginManifest.functions.map((f) => f.name),
                    },
                ];
                // Auth handling
                const mcpAuth = inputs[constants_2.QuestionNames.MCPForDAAuth];
                if (mcpAuth === "OAuthPluginVault") {
                    const authType = inputs[constants_2.QuestionNames.MCPForDAAuthType];
                    if (!authType) {
                        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(teamsfx_api_1.Stage.addPlugin, "MissingMCPAuthType", (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.missingAuthType"), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.missingAuthType")));
                    }
                    const registrationId = `MCP_DA_AUTH_ID_${actionId.toUpperCase()}`;
                    pluginManifest.runtimes[0].auth = {
                        type: "OAuthPluginVault",
                        reference_id: `$\{\{${registrationId}\}\}`,
                    };
                    // Resolve OAuth metadata and inject oauth/register into m365agents.yml
                    try {
                        let authorizationUrl;
                        let tokenUrl;
                        let refreshUrl;
                        if (authType === "oauth") {
                            const metadata = await (0, mcpToolFetcher_1.resolveMCPOAuthMetadata)(inputs[constants_2.QuestionNames.MCPForDAAuthMetadataUrl]);
                            authorizationUrl = metadata.authorizationUrl;
                            tokenUrl = metadata.tokenUrl;
                            refreshUrl = metadata.refreshUrl;
                        }
                        const ymlPath = pathUtils_1.pathUtils.getYmlFilePath(inputs.projectPath);
                        if (ymlPath) {
                            await actionInjector_1.ActionInjector.injectCreateOAuthActionForMCP(ymlPath, authType, actionId, registrationId, mcpServerUrl, authorizationUrl, tokenUrl, refreshUrl);
                        }
                    }
                    catch (error) {
                        mcpWarnings.push({
                            type: "mcpAuthMetadataError",
                            content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.mcpAuthMetadataMissingError", error.message),
                        });
                    }
                }
            }
            await fs_extra_1.default.ensureFile(destinationPluginManifestPath);
            await fs_extra_1.default.writeJSON(destinationPluginManifestPath, pluginManifest, { spaces: 4 });
            const addActionRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.addAction(declarativeCopilotManifestPath, actionId, (0, utils_1.normalizePath)(path.relative(appPackageFolder, destinationPluginManifestPath), true));
            if (addActionRes.isErr()) {
                return (0, teamsfx_api_1.err)(addActionRes.error);
            }
            // Surface warnings for CLI
            if (mcpWarnings.length > 0) {
                for (const warning of mcpWarnings) {
                    context.logProvider.warning(warning.content);
                }
            }
        }
        else {
            const addPluginRes = await (0, helper_1.addExistingPlugin)(declarativeCopilotManifestPath, inputs[constants_2.QuestionNames.PluginManifestFilePath].trim(), inputs[constants_2.QuestionNames.PluginOpenApiSpecFilePath].trim(), actionId, context, teamsfx_api_1.Stage.addPlugin);
            if (addPluginRes.isErr()) {
                return (0, teamsfx_api_1.err)(addPluginRes.error);
            }
            destinationPluginManifestPath = addPluginRes.value.destinationPluginManifestPath;
            const warningMessage = (0, common_1.outputScaffoldingWarningMessage)(addPluginRes.value.warnings);
            context.logProvider.info(warningMessage);
        }
        if (inputs.platform === teamsfx_api_1.Platform.VSCode) {
            const successMessage = (0, localizeUtils_1.getLocalizedString)("core.addPlugin.success.vsc", actionId);
            const viewPluginManifest = (0, localizeUtils_1.getLocalizedString)("core.addPlugin.success.viewPluginManifest");
            void context.userInteraction
                .showMessage("info", successMessage, false, viewPluginManifest)
                .then((userRes) => {
                if (userRes.isOk() && userRes.value === viewPluginManifest) {
                    context.telemetryReporter.sendTelemetryEvent(telemetry_1.TelemetryEvent.ViewPluginManifestAfterAdded);
                    void globalVars_1.TOOLS?.ui?.openFile?.(destinationPluginManifestPath);
                }
            });
        }
        else {
            const successMessage = (0, localizeUtils_1.getLocalizedString)("core.addPlugin.success", actionId, destinationPluginManifestPath);
            void context.userInteraction.showMessage("info", successMessage, false);
        }
        return (0, teamsfx_api_1.ok)(undefined);
    }
    /**
     * Adds an MCP server entry to the project's `.vscode/mcp.json` and returns
     * a marker so the caller (vscode-extension) can open the file and surface
     * the same "start MCP server / Fetch Action" notification used by the
     * "DA with MCP" scaffolding flow.
     */
    async addPluginFromMCP(inputs, context) {
        const projectPath = inputs.projectPath;
        const mcpServerUrl = inputs[constants_2.QuestionNames.MCPForDAServerUrl]?.trim();
        if (!mcpServerUrl) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(teamsfx_api_1.Stage.addPlugin, "MissingMCPServerUrl", (0, localizeUtils_1.getDefaultString)("core.MCPForDA.missingServerUrl"), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.missingServerUrl")));
        }
        // Derive a server entry name from the URL host using the same logic as the
        // "DA with MCP" scaffolding template variable `ServerName`.
        const serverName = (0, helper_1.deriveMCPServerNameFromUrl)(mcpServerUrl);
        const mcpConfigDir = path.join(projectPath, ".vscode");
        const mcpConfigPath = path.join(mcpConfigDir, "mcp.json");
        let mcpConfig = { servers: {} };
        if (await fs_extra_1.default.pathExists(mcpConfigPath)) {
            try {
                const existing = (await fs_extra_1.default.readJSON(mcpConfigPath));
                if (existing && typeof existing === "object") {
                    mcpConfig = {
                        ...existing,
                        servers: existing.servers && typeof existing.servers === "object" ? existing.servers : {},
                    };
                }
            }
            catch {
                // Existing file is unreadable/invalid JSON — overwrite with a fresh config.
                mcpConfig = { servers: {} };
            }
        }
        // Ensure unique server entry name to avoid overwriting an existing one.
        let uniqueName = serverName;
        let suffix = 1;
        while (Object.prototype.hasOwnProperty.call(mcpConfig.servers, uniqueName)) {
            uniqueName = `${serverName}${suffix++}`;
        }
        mcpConfig.servers[uniqueName] = {
            type: "http",
            url: mcpServerUrl,
        };
        await fs_extra_1.default.ensureDir(mcpConfigDir);
        await fs_extra_1.default.writeJSON(mcpConfigPath, mcpConfig, { spaces: 2 });
        context.logProvider.info(`MCP server "${uniqueName}" added to ${path.relative(projectPath, mcpConfigPath)}.`);
        return (0, teamsfx_api_1.ok)({ kind: "mcp", mcpConfigPath });
    }
}
exports.FxCoreDeclarativeAgentPart = FxCoreDeclarativeAgentPart;
tslib_1.__decorate([
    (0, hooks_1.hooks)([
        (0, globalVars_1.ErrorContextMW)({ component: "FxCore", stage: teamsfx_api_1.Stage.installApp }),
        errorHandler_1.ErrorHandlerMW,
        (0, questionMW_1.QuestionMW)("updateActionWithMCP"),
        concurrentLocker_1.ConcurrentLockerMW,
    ]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", Promise)
], FxCoreDeclarativeAgentPart.prototype, "updateActionWithMCP", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([
        (0, globalVars_1.ErrorContextMW)({ component: "FxCore", stage: teamsfx_api_1.Stage.addPlugin }),
        errorHandler_1.ErrorHandlerMW,
        (0, questionMW_1.QuestionMW)("addPlugin"),
        concurrentLocker_1.ConcurrentLockerMW,
    ]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", Promise)
], FxCoreDeclarativeAgentPart.prototype, "addPlugin", null);
//# sourceMappingURL=FxCore.declarativeAgent.js.map